'use client'

import React from 'react'
import Link from 'next/link'
import { Brain, ArrowLeft } from 'lucide-react'

export default function TermsPage() {

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20px 20px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Header with Logo */}
      <div className="relative z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200/70 sticky top-0 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 bg-clip-text text-transparent">
                Best IQ Test
              </span>
            </div>
            
            <Link 
              href="/"
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Main Content */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 md:p-12 shadow-xl border border-white/50">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Terms of Service & Privacy Policy</h1>
            <p className="text-lg text-gray-600">Please read these terms carefully before using our service</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="space-y-8">
              
              {/* Section 1 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">1. Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  By accessing the website at <span className="font-medium text-blue-600">https://bestiqte.st</span>, you are agreeing to be bound by these terms of service, all applicable laws and regulations, and agree that you are responsible for compliance with any applicable local laws. If you do not agree with any of these terms, you are prohibited from using or accessing this site. The materials contained in this website are protected by applicable copyright and trademark law.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">2. Disclaimer</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    The materials on the Best IQ Test website are provided on an 'as is' basis. Best IQ Test makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
                  </p>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                    <p className="font-semibold text-yellow-800 mb-2">Important Notice:</p>
                    <p className="text-yellow-700">
                      Best IQ Test is designed for <strong>informational and entertainment purposes only</strong>. It is <strong>not</strong> intended to be used as a clinical or diagnostic tool for any psychological, educational, or medical purposes. Results should not be used to make significant decisions about personal, educational, or professional matters.
                    </p>
                  </div>
                  
                  <p>
                    Further, Best IQ Test does not warrant or make any representations concerning the accuracy, likely results, or reliability of the use of the materials on its website or otherwise relating to such materials or on any sites linked to this site.
                  </p>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">3. Limitations</h2>
                <p className="text-gray-700 leading-relaxed">
                  In no event shall Best IQ Test or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Best IQ Test's website, even if Best IQ Test or a Best IQ Test authorized representative has been notified orally or in writing of the possibility of such damage. Because some jurisdictions do not allow limitations on implied warranties, or limitations of liability for consequential or incidental damages, these limitations may not apply to you.
                </p>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">4. Accuracy of Materials</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    The materials appearing on Best IQ Test's website could include technical, typographical, or photographic errors. Best IQ Test does not warrant that any of the materials on its website are accurate, complete, or current. Best IQ Test may make changes to the materials contained on its website at any time without notice. However, Best IQ Test does not make any commitment to update the materials.
                  </p>
                  <p>
                    While we strive to provide accurate IQ score estimates, the accuracy of these results is influenced by multiple factors, including but not limited to user variability and test conditions. Therefore, we do not guarantee that the results are a definitive measure of intelligence or reliability across all users.
                  </p>
                </div>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">5. Links</h2>
                <p className="text-gray-700 leading-relaxed">
                  Best IQ Test has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Best IQ Test of the site. Use of any such linked website is at the user's own risk.
                </p>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">6. Modifications</h2>
                <p className="text-gray-700 leading-relaxed">
                  Best IQ Test may revise these terms of service for its website at any time without notice. By using this website you are agreeing to be bound by the then current version of these terms of service.
                </p>
              </section>

              {/* Section 7 - Privacy Policy */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">7. Privacy Policy</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p className="font-medium text-gray-800">Your privacy is important to us.</p>
                  
                  <p>
                    It is Best IQ Test's policy to respect your privacy regarding any information we may collect while operating our website. Accordingly, we have developed this privacy policy in order for you to understand how we collect, use, communicate, disclose, and otherwise make use of personal information. We have outlined our privacy policy below.
                  </p>
                  
                  <ul className="space-y-2 list-disc pl-6">
                    <li>We will collect personal information by lawful and fair means and, where appropriate, with the knowledge or consent of the individual concerned.</li>
                    <li>Before or at the time of collecting personal information, we will identify the purposes for which information is being collected.</li>
                    <li>We will collect and use personal information solely for fulfilling those purposes specified by us and for other ancillary purposes, unless we obtain the consent of the individual concerned or as required by law.</li>
                    <li>Personal data should be relevant to the purposes for which it is to be used, and, to the extent necessary for those purposes, should be accurate, complete, and up-to-date.</li>
                    <li>We will protect personal information by using reasonable security safeguards against loss or theft, as well as unauthorized access, disclosure, copying, use or modification.</li>
                    <li>We will make readily available to customers information about our policies and practices relating to the management of personal information.</li>
                    <li>We will only retain personal information for as long as necessary for the fulfillment of those purposes.</li>
                  </ul>
                </div>
              </section>

              {/* Section 8 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">8. Fulfillment Policy</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    All products and services are delivered via email. Once your purchase is complete, you will receive an email containing the details and any necessary access information.
                  </p>
                  <p>
                    We are happy to refund anyone who isn't satisfied, or even provide your full calculated IQ Score should you be unable to afford it.
                  </p>
                </div>
              </section>

              {/* Section 9 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">9. Use License</h2>
                <p className="text-gray-700 leading-relaxed">
                  By using this site, you agree not to post any part of it, including derivatives, critiques, or any related content, to YouTube or any other platform, for any reason determined by the site owner to be in bad faith. If you do, you are liable to pay for any corrective marketing costs deemed necessary by the site owner.
                </p>
              </section>

            </div>
          </div>

          {/* Bottom Action */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <Link 
              href="/"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Return to Best IQ Test
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}